import { ReactNode, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { api } from "../api/client";
import CommandPalette from "./CommandPalette";
import { useTheme } from "../theme";
import { Icon } from "./Icon";

/**
 * Cada módulo declara el rol mínimo que lo ve.
 *
 * **Esto es cortesía, no seguridad.** Esconder un ítem del menú no protege
 * nada: cualquiera puede llamar al API desde la consola del navegador. La
 * barrera real es `_has_role` en cada handler del backend. Acá solo se evita
 * mostrarle a alguien puertas que no puede abrir.
 */
const NAV = [
  { to: "/pendientes",    label: "Pendientes",    icon: "queue" as const, rol: "miembro" },
  { to: "/historico",     label: "Histórico",     icon: "clock" as const, rol: "miembro" },
  { to: "/metricas",      label: "Métricas",      icon: "chart" as const, rol: "miembro" },
  { to: "/configuracion", label: "Configuración", icon: "gear"  as const, rol: "admin" },
  { to: "/trazabilidad",  label: "Trazabilidad",  icon: "search" as const, rol: "admin", badge: "ADMIN" },
  { to: "/usuarios",      label: "Usuarios",      icon: "users" as const, rol: "admin" },
];

const JERARQUIA: Record<string, number> = { superadmin: 3, admin: 2, miembro: 1 };
export const alcanza = (rol: string | undefined, minimo: string) =>
  (JERARQUIA[rol ?? ""] ?? 0) >= (JERARQUIA[minimo] ?? 0);

interface Props {
  children: ReactNode;
  titulo: string;
  subtitulo?: string;
  email?: string;
  onSignOut?: () => void;
  /** Cuántos depósitos esperan decisión — se muestra junto a "Pendientes". */
  pendientes?: number;
  /** Rol de quien mira. Decide qué módulos aparecen en el menú. */
  rol?: string;
}

export default function Shell({
  children, titulo, subtitulo, email, onSignOut, pendientes, rol,
}: Props) {
  const [colapsado, setColapsado] = useState(false);
  const [paleta, setPaleta] = useState(false);
  const [modo, setModo] = useState<string | null>(null);
  const [pausado, setPausado] = useState(false);
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  // El badge LIVE/CERT es lo que le dice al analista si sus decisiones mutan
  // el Admin de verdad. Va en el header, visible en todas las pantallas.
  useEffect(() => {
    api.getConfig().then((c) => {
      setModo(c.EXECUTION_MODE);
      // Una pausa que no se ve es un pipeline muerto que nadie nota: la cola
      // se vacía sola y parece que no llegaron depósitos. Va en el header y
      // no en Configuración porque el que mira la cola no entra ahí.
      setPausado(c.PROCESAR_DEPOSITOS === false);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaleta((v) => !v);
      }
      if (e.key === "Escape") setPaleta(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="shell">
      <aside className={"sidebar" + (colapsado ? " collapsed" : "")}>
        <div className="sidebar-brand">
          <div className="brand-plate">
            <img src="/logo-coe.png" alt="" />
          </div>
          {!colapsado && (
            <div style={{ minWidth: 0 }}>
              <div className="brand-name">Validación Vouchers</div>
              <div className="brand-ver">COE Prestamype</div>
            </div>
          )}
          <button
            className="icon-btn"
            style={{ marginLeft: "auto", width: 26, height: 26 }}
            onClick={() => setColapsado((v) => !v)}
            title="Colapsar"
            aria-label="Colapsar menú"
          >
            <Icon name="panel" />
          </button>
        </div>

        {!colapsado && (
          <button className="sidebar-search" onClick={() => setPaleta(true)}>
            <Icon name="search" />
            Buscar...
            <kbd>⌘K</kbd>
          </button>
        )}

        <nav>
          {/* Sin `rol` no se filtra nada. El portal se despliega antes que
              el backend, y `/api/me` todavía puede no existir: filtrar sin
              saber el rol dejaría el menú vacío para todos. */}
          {NAV.filter((n) => !rol || alcanza(rol, n.rol)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
              title={colapsado ? n.label : undefined}
            >
              <Icon name={n.icon} />
              {!colapsado && n.label}
              {!colapsado && n.to === "/pendientes" && !!pendientes && (
                <span className="count">{pendientes}</span>
              )}
              {!colapsado && n.badge && <span className="nav-badge">{n.badge}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-foot">
          {!colapsado && (
            <div className="pulse-card">
              <div className="pulse-title">
                <span className="pulse-dot" /> Nuevos depósitos
              </div>
              <div className="pulse-body">
                {pendientes
                  ? `${pendientes} esperando decisión`
                  : "Nada pendiente ahora mismo"}
              </div>
            </div>
          )}
          <div className="user-row">
            <div className="avatar">{(email ?? "?").slice(0, 2).toUpperCase()}</div>
            {!colapsado && (
              <>
                <div className="user-mail" title={email}>{email}</div>
                <button
                  className="icon-btn"
                  style={{ marginLeft: "auto", width: 26, height: 26 }}
                  onClick={onSignOut}
                  title="Cerrar sesión"
                  aria-label="Cerrar sesión"
                >
                  <Icon name="logout" />
                </button>
              </>
            )}
          </div>
        </div>
      </aside>

      <div className="content">
        <header className="header">
          <h1>{titulo}</h1>
          {subtitulo && <span className="sub">{subtitulo}</span>}
          <span className="spacer" />
          {pausado && (
            <span className="chip live" title="No se está analizando ningún voucher nuevo">
              <span className="dot" style={{ background: "currentColor" }} />
              PROCESAMIENTO EN PAUSA
            </span>
          )}
          {modo && (
            <span className={"chip " + (modo === "live" ? "live" : "cert")}>
              <span className="dot" style={{ background: "currentColor" }} />
              {modo === "live" ? "LIVE" : "CERT · simulación"}
            </span>
          )}
          <button
            className="icon-btn"
            onClick={toggle}
            title="Cambiar tema"
            aria-label="Cambiar tema"
          >
            <Icon name={theme === "dark" ? "sun" : "moon"} />
          </button>
        </header>

        <div className="scroll">{children}</div>
      </div>

      {paleta && (
        <CommandPalette
          onClose={() => setPaleta(false)}
          onGo={(to) => { setPaleta(false); navigate(to); }}
        />
      )}
    </div>
  );
}
